// src/contexts/CartContext.tsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { Product, ProductPlan } from '../types/marketplace.types';
import { cartService } from '../services/cartService';

interface CartItem {
    product: Product;
    selectedPlan: ProductPlan;
    couponCode?: string;
}

interface CartContextType {
    items: CartItem[];
    itemCount: number;
    loading: boolean;
    addItem: (product: Product, plan: ProductPlan, couponCode?: string) => Promise<void>;
    removeItem: (productId: string) => Promise<void>;
    clearCart: () => Promise<void>;
    hasItem: (productId: string) => boolean;
    getTotal: () => number;
    syncCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<CartItem[]>([]);
    const [loading, setLoading] = useState(true);

    // ========== CARREGAR CARRINHO AO INICIAR ==========
    useEffect(() => {
        // Carrinho não persiste entre sessões — sempre começa vazio
        setLoading(false);
    }, []);

    const loadCart = async () => {
        try {
            setLoading(true);
            const response = await cartService.getCart();

            // Converter formato do backend para frontend
            const cartItems: CartItem[] = response.items.map((item: any) => {
                // ✅ BUSCAR PLANO COMPLETO NO PRODUTO
                const plan = item.product.plans?.find((p: any) => p._id === item.planId);

                if (!plan) {
                    console.error('Plano não encontrado:', item.planId);
                    return null; // ← Item inválido
                }

                return {
                    product: item.product,
                    selectedPlan: {
                        _id: plan._id,
                        planId: plan.planId || plan._id,
                        durationMonths: plan.durationMonths,
                        priceCents: plan.priceCents
                    } as ProductPlan,
                    // ✅ PRESERVAR CUPOM SE VIER DO BACKEND
                    couponCode: item.couponCode || undefined
                };
            }).filter(Boolean);

            setItems(cartItems as CartItem[]);
        } catch (error) {
            console.error('Erro ao carregar carrinho:', error);
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    // ========== ADICIONAR ITEM ==========
    const addItem = async (product: Product, plan: ProductPlan, couponCode?: string) => {
        try {
            console.log('🛒 [DEBUG] Enviando para backend:', {
                productId: product._id,
                planId: plan._id,
                couponCode: couponCode || 'nenhum',
                plan: plan
            });

            // ✅ PASSAR CUPOM PARA O BACKEND
            await cartService.addItem(product._id, plan._id, couponCode);

            // Atualizar estado local
            setItems(prev => {
                const exists = prev.find(item => item.product._id === product._id);

                if (exists) {
                    return prev.map(item =>
                        item.product._id === product._id
                            ? { product, selectedPlan: plan, couponCode }
                            : item
                    );
                }

                return [...prev, { product, selectedPlan: plan, couponCode }];
            });
        } catch (error) {
            console.error('Erro ao adicionar item:', error);
            throw error;
        }
    };

    // ========== REMOVER ITEM ==========
    const removeItem = async (productId: string) => {
        try {
            // Encontrar item para pegar planId
            const item = items.find(i => i.product._id === productId);
            if (!item) return;

            await cartService.removeItem(productId, item.selectedPlan._id);  // ← CORRIGIDO: usa planId

            // Atualizar estado local
            setItems(prev => prev.filter(item => item.product._id !== productId));
        } catch (error) {
            console.error('Erro ao remover item:', error);
            throw error;
        }
    };

    // ========== LIMPAR CARRINHO ==========
    const clearCart = async () => {
        try {
            await cartService.clearCart();
            setItems([]);
        } catch (error) {
            console.error('Erro ao limpar carrinho:', error);
            throw error;
        }
    };

    // ========== SINCRONIZAR APÓS LOGIN ==========
    const syncCart = async () => {
        try {
            await cartService.syncCart();
            await loadCart(); // Recarregar carrinho
        } catch (error) {
            console.error('Erro ao sincronizar carrinho:', error);
        }
    };

    // ========== VERIFICAR SE TEM ITEM ==========
    const hasItem = (productId: string) => {
        return items.some(item => item.product._id === productId);
    };

    // ========== CALCULAR TOTAL ==========
    const getTotal = () => {
        return items.reduce((sum, item) => sum + item.selectedPlan.priceCents, 0);
    };

    const value: CartContextType = {
        items,
        itemCount: items.length,
        loading,
        addItem,
        removeItem,
        clearCart,
        hasItem,
        getTotal,
        syncCart
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = (): CartContextType => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart deve ser usado dentro de CartProvider');
    }
    return context;
};